import { useState, useCallback, useEffect } from "react";
import { FileDown, FileSpreadsheet, Upload, Calendar, Trash2, Save, ChevronDown, ChevronRight, Sparkles, Download, FolderOpen, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { comptabiliteAPI } from "@/lib/api";
import { DateField } from "@/components/ui/date-field";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

// ── Period Selector (shared) ──────────────────────────────────────────
function PeriodSelector({ mode, setMode, year, setYear, dateFrom, setDateFrom, dateTo, setDateTo }) {
  return (
    <Card className="p-5 mb-6" data-testid="compta-period-card">
      <div className="flex items-center gap-2 mb-4">
        <Calendar size={16} className="text-indigo-500" />
        <h2 className="text-sm font-semibold text-slate-700">Période</h2>
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Mode</label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="w-44 h-9 text-sm" data-testid="compta-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="exercice">Exercice annuel</SelectItem>
              <SelectItem value="custom">Période personnalisee</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {mode === "exercice" ? (
          <div>
            <label className="text-xs text-slate-500 block mb-1">Année</label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-28 h-9 text-sm" data-testid="compta-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Du</label>
              <DateField value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 text-sm w-40" data-testid="compta-date-from" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Au</label>
              <DateField value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 text-sm w-40" data-testid="compta-date-to" />
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

// ── Account Row (collapsible with aux details) ────────────────────────
function AccountRow({ account }) {
  const [open, setOpen] = useState(false);
  const hasAux = account.aux_details && account.aux_details.length > 0;
  const soldeClass = account.solde > 0 ? "text-emerald-600" : account.solde < 0 ? "text-red-500" : "text-slate-500";

  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50/50 text-sm">
        <td className="py-2 px-3 w-8">
          {hasAux ? (
            <button onClick={() => setOpen(!open)} className="text-slate-400 hover:text-slate-700">
              {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : <span className="w-3.5 inline-block" />}
        </td>
        <td className="py-2 px-3 font-mono text-xs text-slate-600">{account.compte_num}</td>
        <td className="py-2 px-3 text-slate-700">{account.label}</td>
        <td className="py-2 px-3 text-xs text-slate-400">{account.category}</td>
        <td className="py-2 px-3 text-right font-mono text-xs">{account.debit.toLocaleString("fr-FR", { minimumFractionDigits: 2 })}</td>
        <td className="py-2 px-3 text-right font-mono text-xs">{account.credit.toLocaleString("fr-FR", { minimumFractionDigits: 2 })}</td>
        <td className={`py-2 px-3 text-right font-mono text-xs font-semibold ${soldeClass}`}>
          {account.solde.toLocaleString("fr-FR", { minimumFractionDigits: 2 })}
        </td>
      </tr>
      {open && hasAux && account.aux_details.map(aux => (
        <tr key={aux.aux_num} className="bg-slate-50/80 text-xs border-b border-slate-50">
          <td className="py-1.5 px-3" />
          <td className="py-1.5 px-3 font-mono text-slate-400 pl-8">{aux.aux_num}</td>
          <td className="py-1.5 px-3 text-slate-500 pl-8">{aux.label}</td>
          <td className="py-1.5 px-3" />
          <td className="py-1.5 px-3 text-right font-mono text-slate-400">{aux.debit.toLocaleString("fr-FR", { minimumFractionDigits: 2 })}</td>
          <td className="py-1.5 px-3 text-right font-mono text-slate-400">{aux.credit.toLocaleString("fr-FR", { minimumFractionDigits: 2 })}</td>
          <td className={`py-1.5 px-3 text-right font-mono font-medium ${aux.solde > 0 ? "text-emerald-500" : aux.solde < 0 ? "text-red-400" : "text-slate-400"}`}>
            {aux.solde.toLocaleString("fr-FR", { minimumFractionDigits: 2 })}
          </td>
        </tr>
      ))}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────
export default function ComptabilitePage() {
  const [mode, setMode] = useState("exercice");
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Import state
  const [parsedAccounts, setParsedAccounts] = useState(null);
  const [importStats, setImportStats] = useState(null);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingFec, setDownloadingFec] = useState(false);
  const [detectedPeriod, setDetectedPeriod] = useState(null); // { date_min, date_max, detected_exercice, year_counts, source, company }
  const [ranYear, setRanYear] = useState(String(CURRENT_YEAR)); // year used for the RAN save

  const getParams = useCallback(() => {
    if (mode === "exercice") {
      return { date_from: `${year}-01-01`, date_to: `${year}-12-31` };
    }
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  }, [mode, year, dateFrom, dateTo]);

  const handleFecExport = () => {
    const params = getParams();
    if (!params.date_from && !params.date_to) {
      toast.error("Sélectionnez une période");
      return;
    }
    window.open(comptabiliteAPI.fecUrl(params), "_blank");
    toast.success("Export FEC lance");
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (importing) {
      toast.info("Analyse déjà en cours, patientez…");
      return;
    }

    const isPdf = file.name.toLowerCase().endsWith(".pdf") || file.type === "application/pdf";
    setImporting(true);
    setParsedAccounts(null);
    setImportStats(null);
    setDetectedPeriod(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = isPdf
        ? await comptabiliteAPI.importBilanPdf(formData)
        : await comptabiliteAPI.importFec(formData);
      setParsedAccounts(data.accounts);
      setImportStats({ total_rows: data.total_rows, errors: data.errors, nb_accounts: data.accounts.length });
      const period = {
        date_min: data.date_min || "",
        date_max: data.date_max || "",
        detected_exercice: data.detected_exercice || "",
        year_counts: data.year_counts || {},
        source: data.source || "fec",
        company: data.company || "",
      };
      setDetectedPeriod(period);
      // Auto-set the RAN year to the detected exercice when present
      if (period.detected_exercice) {
        setRanYear(period.detected_exercice);
      }
      const periodMsg = period.date_min && period.date_max
        ? ` — période ${period.date_min} au ${period.date_max}`
        : "";
      const sourceMsg = isPdf ? "Bilan PDF analysé" : "FEC parsé";
      toast.success(`${sourceMsg} : ${data.accounts.length} comptes détectés${periodMsg}`);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let msg = detail || `Erreur lors de l'analyse du ${isPdf ? "bilan PDF" : "FEC"}`;
      if (err.code === "ECONNABORTED" || status === 504) {
        msg = "Analyse IA trop longue. Le service Gemini est peut-être surchargé, réessayez dans 1-2 minutes.";
      } else if (status === 503) {
        msg = "Service IA temporairement indisponible. Réessayez dans quelques instants.";
      } else if (status === 429) {
        msg = "Quota IA atteint. Patientez quelques minutes puis réessayez.";
      }
      toast.error(msg, { duration: 6000 });
    } finally {
      setImporting(false);
      // Reset the input so user can re-upload the same file if needed
      if (e?.target) e.target.value = "";
    }
  };

  const handleSaveRAN = async () => {
    if (!parsedAccounts) return;
    const exercice = ranYear || (mode === "exercice" ? year : `${dateFrom}_${dateTo}`);
    setSaving(true);
    try {
      await comptabiliteAPI.saveReportANouveau({
        exercice,
        accounts: parsedAccounts,
        source: importStats?.source || "fec",
        warnings: importStats?.warnings || [],
        bilan_debit: importStats?.bilan_debit,
        bilan_credit: importStats?.bilan_credit,
        bilan_ecart: importStats?.bilan_ecart,
      });
      toast.success(`Report à nouveau ${exercice} enregistré`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrément");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadFecAn = async () => {
    if (!parsedAccounts) return;
    setDownloadingFec(true);
    try {
      const res = await comptabiliteAPI.generateAnFec({ accounts: parsedAccounts, exercice: ranYear });
      const blob = new Blob([res.data], { type: "text/tab-separated-values" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `FEC-AN-${ranYear}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`FEC "A nouveau ${ranYear}" telecharge`);
    } catch (err) {
      const msg = err.response?.data instanceof Blob
        ? "Erreur lors de la generation du FEC"
        : err.response?.data?.detail || "Erreur lors de la generation du FEC";
      toast.error(msg);
    } finally {
      setDownloadingFec(false);
    }
  };

  const periodLabel = mode === "exercice" ? `Exercice ${year}` : `${dateFrom || "..."} au ${dateTo || "..."}`;

  // Group parsed accounts by category for summary
  const categorySummary = parsedAccounts ? (() => {
    const cats = {};
    for (const acc of parsedAccounts) {
      if (!cats[acc.category]) cats[acc.category] = { debit: 0, credit: 0, solde: 0, count: 0 };
      cats[acc.category].debit += acc.debit;
      cats[acc.category].credit += acc.credit;
      cats[acc.category].solde += acc.solde;
      cats[acc.category].count += 1;
    }
    return Object.entries(cats).map(([cat, v]) => ({ category: cat, ...v }));
  })() : [];

  return (
    <div className="page-container" data-testid="comptabilite-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
          Comptabilité
        </h1>
        <p className="text-sm text-slate-500 mt-1">Export et import FEC, report à nouveau</p>
      </div>

      <Tabs defaultValue="export" className="w-full">
        <TabsList className="mb-6 flex-wrap h-auto" data-testid="compta-tabs">
          <TabsTrigger value="export" data-testid="compta-tab-export">Export FEC</TabsTrigger>
          <TabsTrigger value="import" data-testid="compta-tab-import">Import FEC / Bilan</TabsTrigger>
          <TabsTrigger value="ran" data-testid="compta-tab-ran">Reports à nouveau</TabsTrigger>
        </TabsList>

        {/* ── Export FEC ─────────────────────────────────────── */}
        <TabsContent value="export">
          <PeriodSelector mode={mode} setMode={setMode} year={year} setYear={setYear} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />

          <Card className="p-5" data-testid="compta-export-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <FileSpreadsheet size={20} className="text-indigo-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Fichier des Écritures Comptables</h3>
                <p className="text-[11px] text-slate-400">Norme DGFiP - Journaux VE (ventes) et HA (achats)</p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Inclut les factures clients, avoirs et factures fournisseurs avec les ecritures comptables associees.
            </p>
            <div className="text-[10px] text-slate-400 mb-3 bg-slate-50 rounded px-2.5 py-1.5">
              Période : {periodLabel}
            </div>
            <Button onClick={handleFecExport} className="gap-2" data-testid="compta-export-fec-btn">
              <FileDown size={15} /> Telecharger FEC (.txt)
            </Button>
          </Card>
        </TabsContent>

        {/* ── Import FEC ou Bilan PDF ─────────────────────────────────────── */}
        <TabsContent value="import">
          <Card className="p-5 mb-6" data-testid="compta-import-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Upload size={20} className="text-amber-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Importer un FEC ou un bilan PDF</h3>
                <p className="text-[11px] text-slate-400">FEC (.txt tab-séparé) ou bilan PDF (analyse IA)</p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-2">
              Chargez un fichier FEC pour extraire les soldes par compte, <span className="font-medium">ou</span> un bilan PDF (l'IA en extrait automatiquement les comptes et soldes).
              Vous pourrez ensuite télécharger un FEC "À nouveau" et/ou enregistrer ces soldes comme report à nouveau.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="file"
                accept=".txt,.csv,.tsv,.pdf,application/pdf"
                onChange={handleFileUpload}
                className="max-w-sm text-sm"
                data-testid="compta-import-file-input"
                disabled={importing}
              />
              {importing && (
                <span className="text-xs text-slate-500 animate-pulse flex items-center gap-1.5">
                  <Sparkles size={12} className="text-indigo-500" />
                  Analyse en cours (PDF : 20-60s)…
                </span>
              )}
            </div>
          </Card>

          {/* Parse results */}
          {importStats && (
            <Card className="p-5 mb-4" data-testid="compta-import-stats">
              {detectedPeriod && (detectedPeriod.date_min || detectedPeriod.date_max) && (
                <div className="mb-4 p-3 rounded-lg bg-indigo-50 border border-indigo-200" data-testid="compta-detected-period">
                  <p className="text-xs text-indigo-900">
                    {detectedPeriod.source === "bilan_pdf" && (
                      <><Sparkles size={11} className="inline mr-1 text-indigo-500" /><span className="font-semibold">Bilan PDF analysé par l'IA</span>{detectedPeriod.company && <> — {detectedPeriod.company}</>}<br /></>
                    )}
                    <span className="font-semibold">Période détectée :</span>{" "}
                    {detectedPeriod.date_min ? new Date(detectedPeriod.date_min).toLocaleDateString("fr-FR") : "?"}{" "}
                    au {detectedPeriod.date_max ? new Date(detectedPeriod.date_max).toLocaleDateString("fr-FR") : "?"}
                    {detectedPeriod.detected_exercice && (
                      <> — exercice principal <span className="font-semibold">{detectedPeriod.detected_exercice}</span></>
                    )}
                  </p>
                </div>
              )}
              {/* Equilibre warning + general warnings */}
              {importStats?.bilan_ecart != null && Math.abs(importStats.bilan_ecart) > 1 && (
                <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-300" data-testid="compta-imbalance-warning">
                  <p className="text-xs text-red-900 font-semibold">
                    ⚠️ Bilan déséquilibré : Débit {(importStats.bilan_debit || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € vs Crédit {(importStats.bilan_credit || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € — écart {importStats.bilan_ecart.toLocaleString("fr-FR", { minimumFractionDigits: 2, signDisplay: "always" })} €
                  </p>
                  <p className="text-[11px] text-red-800 mt-1">
                    Vérifiez les soldes avant d'enregistrer. Vous pouvez aussi importer la balance N-1 détaillée ou le FEC N-1 complet pour un à-nouveau exact.
                  </p>
                </div>
              )}
              {(importStats?.warnings || []).filter(w => !w.toLowerCase().includes("déséquilibré")).map((w, idx) => (
                <div key={idx} className="mb-3 p-2.5 rounded-lg bg-amber-50 border border-amber-200" data-testid={`compta-warning-${idx}`}>
                  <p className="text-[11px] text-amber-900">⚠️ {w}</p>
                </div>
              ))}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Résultat de l'analyse</h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {detectedPeriod?.source === "bilan_pdf"
                      ? `${importStats.nb_accounts} comptes extraits du bilan`
                      : `${importStats.total_rows} ecritures parsees, ${importStats.nb_accounts} comptes`}
                    {importStats.errors > 0 ? `, ${importStats.errors} erreurs` : ""}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-slate-500">Exercice du report :</label>
                    <Select value={ranYear} onValueChange={setRanYear}>
                      <SelectTrigger className="w-24 h-8 text-xs" data-testid="compta-ran-year-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => { setParsedAccounts(null); setImportStats(null); setDetectedPeriod(null); }} className="gap-1.5 text-xs" data-testid="compta-import-clear-btn">
                    <Trash2 size={13} /> Effacer
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDownloadFecAn} disabled={downloadingFec} className="gap-1.5 text-xs" data-testid="compta-download-fec-an-btn">
                    <Download size={13} /> {downloadingFec ? "Génération..." : `Télécharger FEC AN ${ranYear}`}
                  </Button>
                  <Button size="sm" onClick={handleSaveRAN} disabled={saving} className="gap-1.5 text-xs" data-testid="compta-save-ran-btn">
                    <Save size={13} /> {saving ? "Enregistrement..." : `Sauvegarder report à nouveau ${ranYear}`}
                  </Button>
                </div>
              </div>

              {/* Category summary */}
              {categorySummary.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                  {categorySummary.map(cat => (
                    <div key={cat.category} className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider">{cat.category}</p>
                      <p className={`text-sm font-semibold mt-1 ${cat.solde >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                        {cat.solde.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} EUR
                      </p>
                      <p className="text-[10px] text-slate-400">{cat.count} comptes</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Detailed accounts table */}
              <div className="overflow-auto max-h-[500px] border rounded-lg">
                <table className="w-full text-sm" data-testid="compta-accounts-table">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="py-2 px-3 w-8" />
                      <th className="py-2 px-3 text-left text-xs font-medium text-slate-500">Compte</th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-slate-500">Libellé</th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-slate-500">Catégorie</th>
                      <th className="py-2 px-3 text-right text-xs font-medium text-slate-500">Debit</th>
                      <th className="py-2 px-3 text-right text-xs font-medium text-slate-500">Credit</th>
                      <th className="py-2 px-3 text-right text-xs font-medium text-slate-500">Solde</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedAccounts && parsedAccounts.map(acc => (
                      <AccountRow key={acc.compte_num} account={acc} />
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </TabsContent>

        {/* ── Reports à nouveau (liste / detail / suppression) ─── */}
        <TabsContent value="ran">
          <ReportsANouveauTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Reports à nouveau tab ─────────────────────────────────────
function ReportsANouveauTab() {
  const [list, setList] = useState(null); // null = not loaded, [] = empty
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // { exercice: bool }
  const [deletingExercice, setDeletingExercice] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await comptabiliteAPI.getReportANouveau();
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de chargement");
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleDelete = async (exercice) => {
    if (!window.confirm(`Supprimer définitivement le report à nouveau ${exercice} ?`)) return;
    setDeletingExercice(exercice);
    try {
      await comptabiliteAPI.deleteReportANouveau(exercice);
      toast.success(`Report à nouveau ${exercice} supprimé`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur suppression");
    } finally {
      setDeletingExercice("");
    }
  };

  const fmtEUR = (n) => (Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return iso; }
  };

  if (loading && list === null) {
    return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-600" /></div>;
  }

  return (
    <div data-testid="ran-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <FolderOpen size={16} className="text-indigo-500" />
            Reports à nouveau sauvegardés
          </h2>
          <p className="text-xs text-slate-500 mt-1">Soldes d'ouverture par exercice, importés depuis un FEC ou un bilan PDF.</p>
        </div>
        <Button variant="outline" size="sm" onClick={reload} disabled={loading} className="gap-1.5 text-xs" data-testid="ran-reload-btn">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Actualiser
        </Button>
      </div>

      {list && list.length === 0 ? (
        <Card className="p-12 text-center">
          <FolderOpen size={36} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm text-slate-500">Aucun report à nouveau enregistré.</p>
          <p className="text-xs text-slate-400 mt-1">Importez un FEC ou un bilan PDF pour en créer un.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {list && list.map((ran) => {
            const accounts = ran.accounts || [];
            const totalDebit = accounts.reduce((s, a) => s + (a.debit || 0), 0);
            const totalCredit = accounts.reduce((s, a) => s + (a.credit || 0), 0);
            const isOpen = !!expanded[ran.exercice];
            const equilibre = Math.abs(totalDebit - totalCredit) < 1;
            return (
              <Card key={ran.exercice} className="overflow-hidden" data-testid={`ran-card-${ran.exercice}`}>
                <div className="p-4 flex flex-wrap items-center justify-between gap-3 hover:bg-slate-50/50 cursor-pointer" onClick={() => setExpanded({ ...expanded, [ran.exercice]: !isOpen })}>
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center font-bold text-indigo-700 text-sm">
                      {ran.exercice}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-900">Exercice {ran.exercice}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {accounts.length} comptes • enregistré le {fmtDate(ran.created_at)}
                        {!equilibre && <span className="ml-2 text-amber-600">⚠ écart {fmtEUR(totalDebit - totalCredit)} €</span>}
                        {equilibre && accounts.length > 0 && <span className="ml-2 text-emerald-600">✓ équilibré</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-right">
                    <div className="text-xs">
                      <span className="text-slate-400 block">Débit / Crédit</span>
                      <span className="font-mono font-medium text-slate-700">{fmtEUR(totalDebit)} / {fmtEUR(totalCredit)} €</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); handleDelete(ran.exercice); }}
                        disabled={deletingExercice === ran.exercice}
                        data-testid={`ran-delete-${ran.exercice}`}
                        title="Supprimer"
                      >
                        {deletingExercice === ran.exercice ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </Button>
                      {isOpen ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                    </div>
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t bg-slate-50/30">
                    {ran.source === "bilan_pdf" && (
                      <div className="mx-3 mt-3 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                        <p className="text-[11px] text-amber-900">
                          ⚠️ Reconstitution approximative depuis bilan agrégé. Pour un à-nouveau exact, importez la balance N-1 détaillée ou le FEC N-1 complet.
                        </p>
                      </div>
                    )}
                    {ran.bilan_ecart != null && Math.abs(ran.bilan_ecart) > 1 && (
                      <div className="mx-3 mt-3 p-2.5 rounded-lg bg-red-50 border border-red-300">
                        <p className="text-[11px] text-red-900 font-semibold">
                          ⚠️ Déséquilibré au moment de l'enregistrement : écart {Number(ran.bilan_ecart).toLocaleString("fr-FR", { minimumFractionDigits: 2, signDisplay: "always" })} € (D {Number(ran.bilan_debit).toLocaleString("fr-FR")} / C {Number(ran.bilan_credit).toLocaleString("fr-FR")})
                        </p>
                      </div>
                    )}
                    {(ran.warnings || []).filter(w => !w.toLowerCase().includes("déséquilibré")).map((w, idx) => (
                      <div key={idx} className="mx-3 mt-2 p-2 rounded-lg bg-amber-50/60 border border-amber-200">
                        <p className="text-[11px] text-amber-900">⚠️ {w}</p>
                      </div>
                    ))}
                    <div className="overflow-auto max-h-[400px]">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 sticky top-0 z-10">
                          <tr>
                            <th className="py-2 px-3 text-left text-xs font-medium text-slate-500">Compte</th>
                            <th className="py-2 px-3 text-left text-xs font-medium text-slate-500">Libellé</th>
                            <th className="py-2 px-3 text-left text-xs font-medium text-slate-500">Catégorie</th>
                            <th className="py-2 px-3 text-right text-xs font-medium text-slate-500">Débit</th>
                            <th className="py-2 px-3 text-right text-xs font-medium text-slate-500">Crédit</th>
                            <th className="py-2 px-3 text-right text-xs font-medium text-slate-500">Solde</th>
                          </tr>
                        </thead>
                        <tbody>
                          {accounts.map((a) => (
                            <tr key={a.compte_num} className="border-b border-slate-100 hover:bg-white">
                              <td className="py-1.5 px-3 font-mono text-xs text-slate-600">{a.compte_num}</td>
                              <td className="py-1.5 px-3 text-slate-700">{a.label}</td>
                              <td className="py-1.5 px-3 text-xs text-slate-400">{a.category}</td>
                              <td className="py-1.5 px-3 text-right font-mono text-xs">{fmtEUR(a.debit)}</td>
                              <td className="py-1.5 px-3 text-right font-mono text-xs">{fmtEUR(a.credit)}</td>
                              <td className={`py-1.5 px-3 text-right font-mono text-xs font-semibold ${a.solde > 0 ? "text-emerald-600" : a.solde < 0 ? "text-red-500" : "text-slate-400"}`}>
                                {fmtEUR(a.solde)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
